import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';

let predictiveSearchInstanceCount = 0;

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #instanceId = ++predictiveSearchInstanceCount;

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  #resultsObserver = new MutationObserver(() => {
    const activeId = this.refs.searchInput?.getAttribute('aria-activedescendant');
    const activeItem = activeId ? document.getElementById(activeId) : null;
    if (activeItem?.closest('[aria-hidden="true"]')) this.#clearResultSelection();
  });

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.#controller.signal.aborted) this.#controller = new AbortController();
    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    this.#prepareResultOptions();
    this.#clearResultSelection();
    const dialogElement = this.closest('dialog-component')?.querySelector('dialog');
    this.#setComboboxExpanded(Boolean(
      dialogElement?.open
      && !this.refs.predictiveSearchResults.hidden
      && this.refs.predictiveSearchResults.querySelector('[data-search-results]')
    ));
    this.#resultsObserver.observe(this.refs.predictiveSearchResults, {
      attributes: true,
      attributeFilter: ['aria-hidden'],
      subtree: true,
    });

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    if (this.refs.searchInput.value.length === 0 && this.refs.predictiveSearchResults.hidden) {
      this.#emptyStateLoaded = false;
      this.#loadEmptyState();
    } else if (this.refs.searchInput.value.trim().length > 0 && this.refs.predictiveSearchResults.hidden) {
      this.search();
    }

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        if (this.isConnected) this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#abortActiveFetch();
    this.resetSearch.cancel();
    this.#debouncedSearch.cancel();
    this.#resultsObserver.disconnect();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.repeat || event.isComposing || event.defaultPrevented || this.closest('[inert]')) return;
    if ((event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (
      (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0)
      || this.refs.predictiveSearchResults.hidden
    ) {
      this.#loadEmptyState();
    }
    this.#prepareResultOptions();
    this.#setComboboxExpanded(
      !this.refs.predictiveSearchResults.hidden
      && Boolean(this.refs.predictiveSearchResults.querySelector('[data-search-results]'))
    );
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    if (this.refs.searchInput.value.length === 0) this.#resetSearch();
  }

  #getResultItems(includeHidden = false) {
    const resultsRoot = this.refs.predictiveSearchResults.querySelector('[data-search-results]');
    if (!(resultsRoot instanceof HTMLElement)) return [];

    const containers = Array.from(
      resultsRoot.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter(
        (item) => item instanceof HTMLElement && (includeHidden || !item.closest('[aria-hidden="true"]'))
      );

    return /** @type {HTMLElement[]} */ (allItems);
  }

  get #allResultsItems() {
    return this.#getResultItems();
  }

  #prepareResultOptions() {
    this.#getResultItems(true).forEach((item, index) => {
      item.setAttribute('role', 'option');
      if (!item.id) item.id = `predictive-search-option-${this.#instanceId}-${index}`;
      if (!item.hasAttribute('aria-selected')) item.setAttribute('aria-selected', 'false');
      const link = item.querySelector('a[href]');
      const optionLabel = link?.getAttribute('aria-label') || link?.textContent?.trim();
      if (optionLabel) item.setAttribute('aria-label', optionLabel);
      item.querySelectorAll('a[href], button, [tabindex]').forEach((element) => {
        if (!(element instanceof HTMLElement)) return;
        if (!element.hasAttribute('data-predictive-search-tabindex')) {
          element.setAttribute('data-predictive-search-tabindex', element.getAttribute('tabindex') || '');
        }
        element.tabIndex = -1;
      });
    });
  }

  #restoreResultTabStops() {
    this.refs.predictiveSearchResults.querySelectorAll('[data-predictive-search-tabindex]').forEach((element) => {
      if (!(element instanceof HTMLElement)) return;
      const previousTabIndex = element.getAttribute('data-predictive-search-tabindex');
      if (previousTabIndex) {
        element.setAttribute('tabindex', previousTabIndex);
      } else {
        element.removeAttribute('tabindex');
      }
      element.removeAttribute('data-predictive-search-tabindex');
    });
  }

  #clearResultSelection() {
    this.#getResultItems(true).forEach((item) => {
      item.classList.remove('keyboard-focus');
      item.setAttribute('aria-selected', 'false');
    });
    this.refs.searchInput.removeAttribute('aria-activedescendant');
  }

  #setComboboxExpanded(expanded) {
    this.refs.searchInput.setAttribute('aria-expanded', String(expanded));
    if (!expanded) this.refs.searchInput.removeAttribute('aria-activedescendant');
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    const resultItems = this.#allResultsItems;
    if (!resultItems.length) {
      this.#clearResultSelection();
      return;
    }

    this.#prepareResultOptions();
    this.#clearResultSelection();

    const activeItem = resultItems[index];
    if (activeItem) {
      activeItem.setAttribute('aria-selected', 'true');
      if (this.#isKeyboardNavigation) activeItem.classList.add('keyboard-focus');
    }

    if (activeItem) {
      this.refs.searchInput.setAttribute('aria-activedescendant', activeItem.id);
    } else {
      this.refs.searchInput.removeAttribute('aria-activedescendant');
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.target !== this.refs.searchInput || event.isComposing || event.defaultPrevented) return;

    if (event.key === 'Escape') {
      if (this.refs.searchInput.value.length === 0) return;
      event.preventDefault();
      this.#resetSearch();
      return;
    }

    if (
      this.refs.predictiveSearchResults.hidden
      || this.refs.searchInput.getAttribute('aria-expanded') !== 'true'
    ) return;

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
          return;
        }

        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = () => {
    this.#clearResultSelection();
    this.#abortActiveFetch();
    this.refs.predictiveSearchResults.removeAttribute('aria-busy');
    this.#setComboboxExpanded(false);
    this.#setSearchFeedback();

    if (this.refs.searchInput.value.trim().length === 0) {
      this.#resetSearch();
      return;
    }

    this.refs.predictiveSearchResults.hidden = true;
    this.#showResetButton();
    this.#setSearchFeedback('loading');
    this.#debouncedSearch();
  };

  retrySearch = () => {
    this.refs.searchInput.focus();
    this.search();
  };

  #setSearchFeedback(state = '') {
    const error = this.querySelector('[data-search-error]');
    const loading = this.querySelector('[data-search-loading]');
    if (error instanceof HTMLElement) error.hidden = state !== 'error';
    if (loading instanceof HTMLElement) loading.hidden = state !== 'loading';
  }

  async #fetchSectionMarkup(sectionId, url, controller) {
    const sectionUrl = new URL(url);
    sectionUrl.searchParams.set('section_id', sectionId);
    const timeout = setTimeout(() => controller.abort(new DOMException('Search request timed out', 'TimeoutError')), 15000);
    try {
      const response = await fetch(sectionUrl, { signal: controller.signal, headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`Search request failed (${response.status})`);
      return await response.text();
    } finally {
      clearTimeout(timeout);
    }
  }

  #debouncedSearch = debounce(() => {
    const searchTerm = this.refs.searchInput.value.trim();

    if (!searchTerm.length) return;

    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;
    predictiveSearchResults.setAttribute('aria-busy', 'true');

    const abortController = this.#createAbortController();

    const deferredPromise = SearchUpdateEvent.createPromise();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query: searchTerm,
        },
        promise: deferredPromise.promise,
      })
    );

    this.#fetchSectionMarkup(this.dataset.sectionId, url, abortController)
      .then((resultsMarkup) => {
        if (
          abortController.signal.aborted
          || this.#activeFetch !== abortController
          || this.refs.searchInput.value.trim() !== searchTerm
        ) {
          deferredPromise.resolve({ totalCount: 0 });
          return;
        }

        const parsed = new DOMParser().parseFromString(resultsMarkup || '', 'text/html');
        if (!parsed.querySelector('#predictive-search-results [data-search-results]')) {
          throw new Error('Unexpected predictive search response');
        }

        this.#restoreResultTabStops();
        morph(predictiveSearchResults, resultsMarkup);
        predictiveSearchResults.removeAttribute('aria-busy');
        predictiveSearchResults.hidden = false;
        this.#setSearchFeedback();
        this.#prepareResultOptions();
        this.#setComboboxExpanded(true);
        this.#activeFetch = null;

        this.#resetScrollPositions();

        // Count all result items (products, collections, pages, articles, queries)
        const resultCount = this.#allResultsItems.length;
        deferredPromise.resolve({ totalCount: resultCount });
      })
      .catch((error) => {
        if (this.#activeFetch === abortController) {
          predictiveSearchResults.removeAttribute('aria-busy');
          predictiveSearchResults.hidden = true;
          this.#setComboboxExpanded(false);
          this.#setSearchFeedback('error');
          this.#activeFetch = null;
        }
        deferredPromise.resolve({ totalCount: 0 });
        if (!abortController.signal.aborted) console.warn('[predictive-search] Unable to load search results', error);
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup(abortController) {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return this.#fetchSectionMarkup(this.dataset.sectionId, url, abortController);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    this.#abortActiveFetch();
    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  #abortActiveFetch() {
    this.#activeFetch?.abort();
    this.#activeFetch = null;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';
    const hadEmptyState = !predictiveSearchResults.querySelector('[data-search-results]');
    this.#setSearchFeedback();

    this.#currentIndex = -1;
    searchInput.value = '';
    predictiveSearchResults.removeAttribute('aria-busy');
    predictiveSearchResults.hidden = !hadEmptyState;
    this.#setComboboxExpanded(false);
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    try {
      const emptySectionMarkup = await this.#fetchSectionMarkup(emptySectionId, url, abortController);
      const parsedEmptySectionMarkup = new DOMParser()
        .parseFromString(emptySectionMarkup, 'text/html')
        .querySelector('.predictive-search-empty-section');

      if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

      const viewedProducts = RecentlyViewed.getProducts();

      if (viewedProducts.length > 0) {
        const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup(abortController);
        if (recentlyViewedMarkup) {
          const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
          const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
          const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');

          if (recentlyViewedProductsHtml && collectionElement) {
            for (const child of recentlyViewedProductsHtml.children) {
              if (child instanceof HTMLElement) child.setAttribute('ref', 'recentlyViewedWrapper');
            }
            collectionElement.prepend(...recentlyViewedProductsHtml.children);
          }
        }
      }

      if (abortController.signal.aborted || this.#activeFetch !== abortController) return;

      this.#restoreResultTabStops();
      morph(predictiveSearchResults, parsedEmptySectionMarkup);
      predictiveSearchResults.hidden = false;
      this.#setComboboxExpanded(false);
      this.#resetScrollPositions();
    } catch (error) {
      if (!abortController.signal.aborted) {
        this.#emptyStateLoaded = false;
        if (hadEmptyState) predictiveSearchResults.hidden = false;
        console.warn('[predictive-search] Unable to reset search results', error);
      }
    } finally {
      if (this.#activeFetch === abortController) {
        predictiveSearchResults.removeAttribute('aria-busy');
        this.#activeFetch = null;
      }
    }
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
